import React, { useState } from 'react';
import { REVIEW_SHOTS, ROB_STORY, VIDEO_REVIEWS } from '../course/courseContent';
import { TRUSTPILOT_URL } from '../course/StarRating';
import { selectTrustBarStats } from './trustBarProof';
import './TrustBar.css';

type VideoReview = { youtubeId: string; name: string; title: string };

function TrustBarVideo({ review }: { review: VideoReview }) {
  const [playing, setPlaying] = useState(false);
  return (
    <article className="trust-bar__video" data-testid="trust-bar-video">
      {playing ? (
        <iframe
          title={review.title}
          src={`https://www.youtube-nocookie.com/embed/${review.youtubeId}?autoplay=1&rel=0`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          className="trust-bar__poster"
          onClick={() => setPlaying(true)}
          aria-label={`Play ${review.name} video review`}
        >
          <img
            src={`https://i.ytimg.com/vi/${review.youtubeId}/hqdefault.jpg`}
            alt=""
            width={480}
            height={360}
            loading="lazy"
          />
          <span className="trust-bar__play" aria-hidden="true" />
        </button>
      )}
    </article>
  );
}

/**
 * Reusable social proof for the challenge call funnel: reach numbers, real
 * Trustpilot screenshots, and student video reviews. Everything renders from
 * course content; nothing here is retyped or invented.
 */
export default function TrustBar({
  heading = 'Real students. Real reviews.',
}: {
  heading?: string;
}) {
  const stats = selectTrustBarStats(ROB_STORY.credentials);

  return (
    <section className="trust-bar" aria-labelledby="trust-bar-heading" data-testid="trust-bar">
      <h2 id="trust-bar-heading" className="trust-bar__heading">
        {heading}
      </h2>

      {stats.length > 0 && (
        <ul className="trust-bar__stats" data-testid="trust-bar-stats">
          {stats.map((stat) => (
            <li key={stat.label}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="trust-bar__shots" aria-label="Trustpilot reviews">
        {REVIEW_SHOTS.map((shot) => (
          <a
            key={shot.src}
            className="trust-bar__shot"
            href={TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trust-bar-review-shot"
          >
            <img
              src={shot.src}
              alt={shot.alt}
              width={shot.width}
              height={shot.height}
              loading="lazy"
            />
          </a>
        ))}
      </div>

      <h3 className="trust-bar__subheading">Student video reviews</h3>
      <div className="trust-bar__videos">
        {VIDEO_REVIEWS.map((review) => (
          <TrustBarVideo key={review.youtubeId} review={review} />
        ))}
      </div>

      <a
        className="trust-bar__link"
        href={TRUSTPILOT_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Read all reviews on Trustpilot
      </a>
    </section>
  );
}
